/**
 * AutoComplete Component - Theme-aware autocomplete input with suggestions
 * Replaces Ant Design AutoComplete for playbook compliance
 */

const React = (globalThis as any).React;
const { useState, useEffect, useRef, forwardRef, useMemo, useImperativeHandle } = React;

import { Input } from './Input';
import { styles } from '../styles';

export interface AutoCompleteOption {
  value: string;
  label: string | React.ReactNode;
}

export interface AutoCompleteProps {
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  onSelect?: (value: string) => void;
  options?: AutoCompleteOption[];
  placeholder?: string;
  disabled?: boolean;
  open?: boolean;
  filterOption?: boolean | ((inputValue: string, option: AutoCompleteOption) => boolean);
  style?: React.CSSProperties;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  children?: never; // AutoComplete doesn't accept children - uses options prop instead
}

export const AutoComplete = (forwardRef as any)((props: AutoCompleteProps, ref: React.Ref<HTMLInputElement>) => {
  const {
    value = '',
    onChange,
    onSearch,
    onSelect,
    options = [],
    placeholder,
    disabled = false,
    style,
    prefix,
    suffix,
    id,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    onFocus,
    onBlur,
    open: controlledOpen,
    filterOption = true,
  } = props;

  const [internalOpen, setInternalOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null) as React.MutableRefObject<HTMLDivElement | null>;
  const inputRef = useRef(null) as React.MutableRefObject<HTMLInputElement | null>;
  const listRef = useRef(null) as React.MutableRefObject<HTMLUListElement | null>;

  // Use controlled open if provided, otherwise use internal state
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

  // Filter options based on filterOption prop
  const filteredOptions = useMemo(() => {
    if (!value || !filterOption) return options;
    
    if (typeof filterOption === 'function') {
      return options.filter(option => filterOption(value, option));
    }
    
    // Default filtering: case-insensitive match
    const lowerValue = value.toLowerCase();
    return options.filter(option => {
      // Handle label: can be string or React.ReactNode
      const labelText = typeof option.label === 'string' 
        ? option.label 
        : option.value; // Fallback to value if label is ReactNode
      
      return labelText.toLowerCase().includes(lowerValue) ||
        option.value.toLowerCase().includes(lowerValue);
    });
  }, [value, options, filterOption]);

  // Handle input change
  const handleChange = (newValue: string) => {
    onChange?.(newValue);
    onSearch?.(newValue);
    if (controlledOpen === undefined) {
      setInternalOpen(newValue.length > 0 && filteredOptions.length > 0);
    }
    setHighlightedIndex(-1);
  };

  // Handle option selection
  const handleSelect = (option: AutoCompleteOption) => {
    onChange?.(option.value);
    onSelect?.(option.value);
    if (controlledOpen === undefined) {
      setInternalOpen(false);
    }
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || filteredOptions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev: number) => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev: number) => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        if (controlledOpen === undefined) {
          setInternalOpen(false);
        }
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Handle focus
  const handleFocus = () => {
    onFocus?.();
    if (controlledOpen === undefined && value && filteredOptions.length > 0) {
      setInternalOpen(true);
    }
  };

  // Handle blur
  const handleBlur = (e: React.FocusEvent) => {
    // Delay to allow option click to register
    setTimeout(() => {
      if (controlledOpen === undefined) {
        setInternalOpen(false);
      }
      setHighlightedIndex(-1);
      onBlur?.();
    }, 200);
  };

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        if (controlledOpen === undefined) {
          setInternalOpen(false);
        }
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, controlledOpen]);

  // Expose ref to parent
  useImperativeHandle(ref, () => inputRef.current);

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    ...style,
  };

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1000,
    marginTop: '4px',
    background: 'var(--card-bg)',
    border: '1px solid var(--card-border)',
    borderRadius: '8px',
    boxShadow: 'var(--shadow-lg)',
    maxHeight: '300px',
    overflowY: 'auto' as const,
    scrollbarWidth: 'thin' as const,
    scrollbarColor: 'var(--border-color) transparent',
  };

  const optionStyle = (index: number): React.CSSProperties => ({
    padding: '12px 16px',
    cursor: 'pointer',
    background: highlightedIndex === index 
      ? 'var(--main-accent)' 
      : 'transparent',
    color: highlightedIndex === index 
      ? 'var(--button-text)' 
      : 'var(--main-text)',
    transition: 'background 0.2s, color 0.2s',
    borderBottom: index < filteredOptions.length - 1 
      ? '1px solid var(--border-color)' 
      : 'none',
  });

  return React.createElement('div', { 
    ref: containerRef,
    style: containerStyle 
  },
    React.createElement(Input, {
      ref: inputRef,
      value,
      onChange: handleChange,
      placeholder,
      disabled,
      prefix,
      suffix,
      id,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      onKeyDown: handleKeyDown,
      onFocus: handleFocus,
      onBlur: handleBlur,
      style: {
        width: '100%',
        ...(style && { width: style.width }),
      },
    }),
    isOpen && filteredOptions.length > 0 && React.createElement('ul', {
      ref: listRef,
      role: 'listbox',
      'aria-label': 'Autocomplete options',
      style: dropdownStyle,
    },
      filteredOptions.map((option: AutoCompleteOption, index: number) =>
        React.createElement('li', {
          key: option.value,
          role: 'option',
          'aria-selected': highlightedIndex === index,
          style: optionStyle(index),
          onMouseDown: (e: any) => {
            e.preventDefault(); // Prevent input blur
            handleSelect(option);
          },
          onMouseEnter: () => setHighlightedIndex(index),
        }, option.label)
      )
    )
  );
});

